/* customCohortsSlice.ts */

/* Redux-Toolkit */
import {
  createSlice,
  current,
  PayloadAction,
} from "@reduxjs/toolkit";

/* uuid */
import { v4 as uuidv4 } from "uuid";

/* Utils */
import {
  validateCohortName,
  findCohortById,
  findCohortIndexById,
  transformCohortListToString,
  checkStatus,
} from "src/redux/features/custom-cohorts/helper";

/* Interfaces */
import {
  CustomCohortsState,
  CustomCohortsProps,
  Status,
  BasicProps,
  LogicalProps,
} from "src/shared/interfaces/custom-cohorts/CustomCohorts";

import { BasicErrorProps } from "src/shared/interfaces/error/Error";

export const customCohort: CustomCohortsProps = {
  id: uuidv4(),
  name: "Custom cohort",
  createdAt: undefined,
  modifiedAt: undefined,
  status: undefined,
  data: undefined,
  show: true,
};

const initialState: CustomCohortsState = {
  currentDiseaseId: undefined,
  customCohortsByDisease: {},
  customCohort,
};

export const customCohortsSlice = createSlice({
  name: "customCohorts",
  initialState,
  reducers: {
    /**
     * Set the selected disease and create initial item.
     *
     * @param {CustomCohortsState} state - The current state.
     * @param {PayloadAction<string>} action - The id of the selected disease.
     */
    setCurrentDisease: (
      state: CustomCohortsState,
      action: PayloadAction<string>
    ): void => {
      state.currentDiseaseId = action.payload;

      // If disease does not exist, create an item with cohorts and customCohortsStr
      if (!state.customCohortsByDisease[action.payload]) {
        state.customCohortsByDisease[action.payload] = {
          cohorts: [],
          customCohortsStr: undefined,
        };
      }
    },

    /**
     * Updates the name of the currently selected custom cohort.
     *
     * @param {CustomCohortsState} state - The current state.
     * @param {PayloadAction<CustomCohortsProps>} action - The new name of the cohort.
     * @returns {void}
     */
    setName: (
      state: CustomCohortsState,
      action: PayloadAction<string>
    ): void => {
      state.customCohort.name = action.payload;
    },

    /**
     * Reset the state 'customCohort to the initial value.
     * This is used when 'mainCondition' has changed,
     * or when a new custom cohort is created.
     *
     * @param {CustomCohortState} state
     * @returns {void}
     */
    setDefaultState: (state: CustomCohortsState): void => {
      state.customCohort = customCohort;
    },

    /**
     * Adds a new cohort or updates an existing one based in its id.
     *
     * @param {CustomCohortsState} state - The current state.
     * @param {PayloadAction<CustomCohortsProps>} action - The cohort to be added or updated.
     * @returns {void}
     */
    upsertCustomCohort: (
      state: CustomCohortsState,
      action: PayloadAction<CustomCohortsProps>
    ): void => {
      const diseaseId = state.currentDiseaseId;
      if (!diseaseId) return;

      const index = findCohortIndexById(
        state,
        action.payload.id
      );

      // If cohort exists in state.customCohortsByDisease, update cohort
      if (index !== -1) {
        console.log(
          current(
            state.customCohortsByDisease[diseaseId].cohorts[
              index
            ]
          )
        );
        const updatedCohort = (state.customCohortsByDisease[
          diseaseId
        ].cohorts[index] = {
          ...state.customCohortsByDisease[diseaseId]
            .cohorts[index],
          ...action.payload,
          modifiedAt: new Date().toISOString(),
          status: Status.MODIFIED,
        });

        /**
         * Set 'customCohort' to the updated one,
         * in order to immediately reflect changes in the UI.
         */
        state.customCohort = updatedCohort;
        // Otherwise, create a new cohort and add it to state.customCohortsByDisese
      } else {
        validateCohortName(
          diseaseId,
          state,
          action.payload.name
        );

        const newCohort: CustomCohortsProps = {
          ...action.payload,
          id: uuidv4(),
          createdAt: new Date().toISOString(),
          status: Status.SAVED,
        };

        state.customCohortsByDisease[
          diseaseId
        ].cohorts.push(newCohort);

        // Set state.customCohort to newCohort to show the new created cohort in UI
        state.customCohort = newCohort;
      }

      // Compute customCohortsStr after upsertCustomCohort
      state.customCohortsByDisease[
        diseaseId
      ].customCohortsStr = transformCohortListToString(
        state.customCohortsByDisease[diseaseId].cohorts
      );
    },

    /**
     *
     * @param {CustomCohortsState} state - The current state.
     * @param {string} action - The id of the cohort to toggle.
     * @returns {void}
     */
    toggleCohortSelection: (
      state: CustomCohortsState,
      action: PayloadAction<string>
    ): void => {
      const cohortId = action.payload;
      const diseaseId = state.currentDiseaseId;
      if (!diseaseId) return;

      const cohort = state.customCohortsByDisease[
        diseaseId
      ].cohorts.find((cohort) => cohort.id === cohortId);

      if (cohort) {
        // Toggle cohort.show - if true set to false and vice versa
        state.customCohortsByDisease[diseaseId].cohorts =
          state.customCohortsByDisease[
            diseaseId
          ].cohorts.map((cohort) =>
            cohort.id === cohortId
              ? { ...cohort, show: !cohort.show }
              : cohort
          );
      }

      const visibleCohorts = state.customCohortsByDisease[
        diseaseId
      ].cohorts.filter((cohort) => cohort.show);

      // After toggle, customCohortsStr is updated
      state.customCohortsByDisease[
        diseaseId
      ].customCohortsStr =
        visibleCohorts.length > 0
          ? transformCohortListToString(visibleCohorts)
          : undefined;
    },

    /**
     * Set the current cohort based on its id.
     *
     * This is used to update state and UI when a cohort is selected.
     * The current cohort can be set, either by clicking the cohort wihtin the cohort list,
     * or while editing.
     *
     * @param {CustomCohortsState} state
     * @param {PayloadAction<{ id: string; status?: Status }>} action
     * @returns {void}
     */
    setCurrentCohort: (
      state: CustomCohortsState,
      action: PayloadAction<{ id: string; status?: Status }>
    ): void => {
      const diseaseId = state.currentDiseaseId;
      if (!diseaseId) return;

      const currentCohort = findCohortById(
        diseaseId,
        state,
        action.payload.id
      );

      if (!currentCohort) return;

      // Check status of current cohort
      const status = checkStatus(
        action.payload.status,
        currentCohort.modifiedAt
      );

      state.customCohortsByDisease[diseaseId].cohorts =
        state.customCohortsByDisease[diseaseId].cohorts.map(
          (cohort) =>
            cohort.id === action.payload.id
              ? { ...cohort, status: status }
              : cohort
        );

      state.customCohort = {
        ...currentCohort,
        status: status,
      };
    },

    setStateSegments: (
      state: CustomCohortsState,
      action: PayloadAction<
        BasicProps | LogicalProps | undefined
      >
    ) => {
      state.customCohort.data = action.payload;
    },

    /**
     * Deletes a cohort based on its id. If there are remaining cohorts, the first one
     * is set as the current cohort - otherwise, a default cohort is set.
     *
     * @param {CustomCohortsState} state - The current state.
     * @param {PayloadAction<string>} action - The ID of the cohort to delete.
     * @returns {void}
     */
    deleteCustomCohort: (
      state: CustomCohortsState,
      action: PayloadAction<string>
    ): void => {
      const diseaseId = state.currentDiseaseId;
      if (!diseaseId) return;

      state.customCohortsByDisease[diseaseId].cohorts =
        state.customCohortsByDisease[
          diseaseId
        ].cohorts.filter(
          (cohort) => cohort.id !== action.payload
        );

      if (
        state.customCohortsByDisease[diseaseId].cohorts
          .length === 0
      ) {
        state.customCohortsByDisease[
          diseaseId
        ].customCohortsStr = undefined;
      } else {
        state.customCohortsByDisease[
          diseaseId
        ].customCohortsStr = transformCohortListToString(
          state.customCohortsByDisease[diseaseId].cohorts
        );
      }

      /**
       * After deleting cohort, state.customCohort is set to first entry
       * in state.customCohortsByDisease, otherwise initial cohort is used.
       */
      state.customCohort =
        state.customCohortsByDisease[diseaseId].cohorts
          .length > 0
          ? state.customCohortsByDisease[diseaseId]
              .cohorts[0]
          : customCohort;
    },

    /**
     * Clears all custom cohorts for the current disease.
     *
     * @param {CustomCohortsState} state - The current state.
     * @returns {void}
     */
    clearCustomCohortList: (
      state: CustomCohortsState
    ): void => {
      const diseaseId = state.currentDiseaseId;
      if (!diseaseId) return;

      state.customCohortsByDisease[diseaseId].cohorts = [];
      state.customCohortsByDisease[
        diseaseId
      ].customCohortsStr = undefined;
      state.customCohort = customCohort;
    },

    /**
     * Sets an error in the state.
     *
     * @param {CustomCohortsState} state - The current state.
     * @param {PayloadAction<BasicErrorProps | undefined>} action - The error details.
     * @returns {void}
     */
    setError: (
      state: CustomCohortsState,
      action: PayloadAction<BasicErrorProps | undefined>
    ) => {
      state.error = action.payload!;
    },

    /**
     * Clears any existing errors in the state.
     *
     * @param {CustomCohortsState} state - The current state.
     * @returns {void}
     */
    clearError: (state: CustomCohortsState) => {
      state.error = undefined;
    },
  },
});

/* Actions */
export const {
  setCurrentDisease,
  setName,
  setDefaultState,
  setStateSegments,
  upsertCustomCohort,
  toggleCohortSelection,
  setCurrentCohort,
  deleteCustomCohort,
  clearCustomCohortList,
  setError,
  clearError,
} = customCohortsSlice.actions;

/* Reducer */
export default customCohortsSlice.reducer;
